/**
 * Sessions feature - workspace state persistence across app restarts
 */

// Types
export type {
  Session,
  SerializedTab,
  SessionConnection,
  SessionConnectionStatus,
  SerializedQueryTabContent,
  SerializedTableEditorTabContent,
  ConnectionDecision,
  FailedConnectionDecision,
} from './types/sessionTypes';

export {
  isSerializedQueryContent,
  isSerializedTableEditorContent,
} from './types/sessionTypes';

// Store
export { useSessionStore } from './store/sessionStore';
export { useSessionsUIStore } from './store/uiStore';

// Components
export { SessionsDialog } from './components/SessionsDialog';
export { SessionListCard } from './components/SessionListCard';
export { StartFreshConfirmDialog } from './components/StartFreshConfirmDialog';
export { SessionValidationDialog } from './components/SessionValidationDialog';
export { ConnectionValidationStatus } from './components/ConnectionValidationStatus';
export { FailedConnectionPrompt } from './components/FailedConnectionPrompt';
export { DisconnectedConnectionBanner } from './components/DisconnectedConnectionBanner';

// Services
export {
  initializeSessionAutoSave,
  saveSessionImmediately,
  resetSessionAutoSave,
  pauseSessionAutoSave,
  resumeSessionAutoSave,
} from './services/sessionAutoSave';

export {
  validateSessionConnections,
  applyValidationResults,
  getFailedConnections,
  allConnectionsSucceeded,
  applyConnectionDecisions,
  allDecisionsMade,
  createPendingDecisions,
  createExclusionDecisions,
  type ValidationResult,
  type ValidationProgress,
  type ProgressCallback,
} from './services/connectionValidationService';

export { applyResumeExclusions } from './services/resumeExclusions';

// Utils
export { getConnectedConnectionIds } from './utils/sessionDisplayUtils';

// Expose stores on window in test mode for E2E testing
import { useSessionStore } from './store/sessionStore';
import { useAppStore as useAppStoreForTest } from '../../store/appStore';
import { useSessionsUIStore } from './store/uiStore';
import { useWorkspaceStore } from '../workspace/core/store/workspaceStore';
import { useDatabaseUIStore } from '../database-objects/store/uiStore';
import { useDatabaseStore } from '../database-objects/store/databaseStore';
import { useConnectionsUIStore } from '../connections/store/uiStore';
import { useConnectionStore } from '../connections/store/connectionStore';
import { useTerminalStore } from '../terminal/store/terminalStore';
import { useAiLockStore } from '../terminal/store/aiLockStore';
import { useAiSelectionStore } from '../terminal/store/aiSelectionStore';
import { useLicenseStore } from '../../core/licensing/store/licenseStore';
import { useAuthStore } from '../../core/licensing/store/authStore';
import { useSubscriptionStore } from '../../core/licensing/store/subscriptionStore';
import { useProUpgradePromptStore } from '../../core/licensing/store/proUpgradePromptStore';
import { useContextSelectionStore } from '../toolbar-context/store/contextSelectionStore';
import { initializeSessionAutoSave, resetSessionAutoSave } from './services/sessionAutoSave';
import { clearLazyConnectState } from './services/lazyConnect';
import { useSidebarStore } from '../database-objects/store/sidebarStore';
import { useSqlSnippetsStore } from '../sql-snippets/store/sqlSnippetsStore';
import { useScriptBrowserStore } from '../script-browser/store/scriptBrowserStore';
import { useMountedFolderIndexStore } from '../script-browser/store/mountedFolderIndexStore';
import { useAiChatStore } from '../ai-chat/store/aiChatStore';
import { usePackagePinsStore } from '../workspace/package-explorer/store/packagePinsStore';
import { useQueryHistoryStore } from '../query-history/store/queryHistoryStore';
import { useKeybindingsStore } from '../keybindings/store/keybindingsStore';
import { useTabHistoryStore } from '../tab-history/store/tabHistoryStore';
import { useTableExplorerDialogStore } from '../workspace/table-explorer/store/tableExplorerDialogStore';
import { useMetaInfoManagerDialogStore } from '../workspace/common/components/meta-info/metaInfoManagerDialogStore';
import { useDatabaseBlueprintSetupStore } from '../database-blueprint/store/databaseBlueprintSetupStore';
import { useSyncDataProgressDialogStore } from '../workspace/database-blueprint/store/syncDataProgressDialogStore';
import { usePendingRetryStore } from '../workspace/connection-banner/store/pendingRetryStore';
import { useSchemaCompareOptionDefaultsStore } from '../workspace/schema-compare/store/schemaCompareOptionDefaultsStore';
import { openSchemaCompareWithFolderAsSource } from '../workspace/schema-compare/services/schemaCompareNavigation';
import { injectPgSchemaForTest, dropPgSchema } from '../connections/services/language-services/pgSchemaCache';
import { invalidateColumns as invalidateInsertHintColumns } from '../workspace/common/utils/insertHintColumnResolver';
import { injectRoutineForTest, invalidateRoutines as invalidateRoutineCache } from '../workspace/common/utils/routineParameterResolver';
import { useVqeDdlStore } from '../workspace/visual-ddl-editor/store';
import { useVqeStore } from '../workspace/visual-query-editor/store/vqeStore';
import { openDataImportTab } from '../workspace/data-import/openDataImportTab';
import { useDataImportStore } from '../workspace/data-import/store/dataImportStore';
import { routeDroppedFiles } from '../file-drop/fileDropRouter';
import { executeAction as executeObjectAction } from '../shared/object-actions/actionRegistry';
import { storageDownloadRequestProbe } from '../workspace/storage-explorer/services/storageDownload';

// Callbacks for App component to register for reset
let appResetCallbacks: (() => void)[] = [];

/**
 * Register a callback to be called when test reset is triggered.
 * Used by App component to reset internal refs.
 */
export function registerAppResetCallback(callback: () => void): () => void {
  appResetCallbacks.push(callback);
  return () => {
    appResetCallbacks = appResetCallbacks.filter(cb => cb !== callback);
  };
}

/**
 * Trigger app reset - called by test fixtures.
 * Resets all stores and notifies App component to reset internal state.
 */
export function triggerTestReset(): void {
  // Reset auto-save state (cancel pending saves, clear subscription)
  resetSessionAutoSave();
  // `liveSessionId` records that the live workspace represents a session, and
  // it decides whether a save may overwrite that session's tabs. A reset that
  // left it set would hand the next spec a workspace/session pairing that no
  // longer exists — and, worse, would make a spec's un-restored precondition
  // depend on which spec ran before it on the same worker.
  useSessionStore.setState({ liveSessionId: null });
  // Lazy connect's deferred set is module-level, so it outlives any store
  // reset: leaving it filled lets a later spec's tab click open a connection
  // an earlier spec's restore deferred.
  clearLazyConnectState();
  // F07: the last-used Schema Compare options are persisted and now SEED every
  // newly opened setup dialog, so leaving them set lets one spec decide what
  // the next spec's fresh dialog starts from.
  //
  // ⚠️ This is NOT what protects the schema-compare specs. `resetApp` in
  // `src/test/e2e/setup/appFixture.ts` deliberately does not route through
  // this function (see its "don't use triggerAppReset" comment) and resets
  // stores itself — the load-bearing reset for this store lives there. This
  // one only covers the four session specs that call `triggerAppReset`.
  useSchemaCompareOptionDefaultsStore.setState({ lastUsedByEngine: {} });
  // Notify all registered callbacks (App component will reset its refs)
  appResetCallbacks.forEach(cb => cb());
}

declare global {
  interface Window {
    __TEST_STORES__?: {
      appStore: typeof useAppStoreForTest;
      sessionStore: typeof useSessionStore;
      sessionsUIStore: typeof useSessionsUIStore;
      workspaceStore: typeof useWorkspaceStore;
      databaseUIStore: typeof useDatabaseUIStore;
      databaseStore: typeof useDatabaseStore;
      connectionsUIStore: typeof useConnectionsUIStore;
      connectionStore: typeof useConnectionStore;
      terminalStore: typeof useTerminalStore;
      aiLockStore: typeof useAiLockStore;
      aiSelectionStore: typeof useAiSelectionStore;
      licenseStore: typeof useLicenseStore;
      authStore: typeof useAuthStore;
      subscriptionStore: typeof useSubscriptionStore;
      proUpgradePromptStore: typeof useProUpgradePromptStore;
      contextSelectionStore: typeof useContextSelectionStore;
      sidebarStore: typeof useSidebarStore;
      sqlSnippetsStore: typeof useSqlSnippetsStore;
      scriptBrowserStore: typeof useScriptBrowserStore;
      mountedFolderIndexStore: typeof useMountedFolderIndexStore;
      aiChatStore: typeof useAiChatStore;
      packagePinsStore: typeof usePackagePinsStore;
      queryHistoryStore: typeof useQueryHistoryStore;
      // Persisted settings the E2E harness restores between spec files
      // (src/test/e2e/setup/appSettingsOwnership.ts).
      keybindingsStore: typeof useKeybindingsStore;
      tabHistoryStore: typeof useTabHistoryStore;
      tableExplorerDialogStore: typeof useTableExplorerDialogStore;
      vqeDdlStore: typeof useVqeDdlStore;
      /** Exposed so VQE specs can reset per-session state (e.g. `sessionSuccessShown`). */
      vqeStore: typeof useVqeStore;
      metaInfoManagerDialogStore: typeof useMetaInfoManagerDialogStore;
      databaseBlueprintSetupStore: typeof useDatabaseBlueprintSetupStore;
      syncDataProgressDialogStore: typeof useSyncDataProgressDialogStore;
      pendingRetryStore: typeof usePendingRetryStore;
      // F07 — persisted last-used compare options; e2e specs reset it so one
      // spec's object-type choice can't decide the next spec's starting state.
      schemaCompareOptionDefaultsStore: typeof useSchemaCompareOptionDefaultsStore;
      // W3.4/F07 fix round 4 — the PRODUCTION "Apply schema to DB" entry point,
      // exposed for e2e for the same reason `openDataImportTab` is: driving it
      // through the Database Blueprint tab would need a mounted blueprint and a
      // full toolbar walk, and the specs that matter here are about what happens
      // to the tab AFTERWARDS. Seeding the tab with `addTab` instead is what let
      // three rounds of regression tests skip the code that actually loses the
      // scope-cap marker, so the marker has to be produced by this function.
      openSchemaCompareWithFolderAsSource: typeof openSchemaCompareWithFolderAsSource;
      injectPgSchemaForTest: typeof injectPgSchemaForTest;
      dropPgSchema: typeof dropPgSchema;
      invalidateInsertHintColumns: typeof invalidateInsertHintColumns;
      injectRoutineForTest: typeof injectRoutineForTest;
      invalidateRoutineCache: typeof invalidateRoutineCache;
      // S4.T5 (Data Import e2e): opens the wizard tab exactly like a real entry
      // point would (S8 hasn't wired one into the UI yet) + exposes the wizard's
      // per-tab store so tests can seed/inspect runtime-only state (e.g. a
      // synthetic `analysisComplete: false` snapshot for the Start-gate scenario)
      // without a parallel test-only mechanism.
      openDataImportTab: typeof openDataImportTab;
      dataImportStore: typeof useDataImportStore;
      // S8.T2 (file-drop e2e): drives the SAME `decideDropAction`-routing
      // `useFileDrop`'s real DOM handler uses, but with directly-supplied
      // `{name, path}` entries — Electron's contextBridge does not allow a
      // page script to reassign `window.electronAPI.getPathForFile`, so a
      // synthetic drag/drop event can't fake a native path for a test fixture
      // (see the doc comment on `fileDropRouter.ts` for the confirmation).
      routeDroppedFiles: typeof routeDroppedFiles;
      storageDownloadRequestProbe: typeof storageDownloadRequestProbe;
      /**
       * The PRODUCTION object-action executor (Object Explorer's context menu
       * runs the same function). Exposed for the real-DB Table Designer specs
       * so "Design Table" can be driven end-to-end: `designTableAction` →
       * `synthesizeAlterTableBuffer` → the engine script provider's
       * `generateTableScript('create')`. Seeding `initialSql` with `addTab`
       * instead is exactly what let those specs assert against a baseline the
       * app never emits (PR #76 review: the MySQL baseline dropped COMMENT /
       * COLLATE / GENERATED / UNSIGNED), so the baseline has to come from here.
       */
      executeObjectAction: typeof executeObjectAction;
      triggerAppReset: () => void;
      // `triggerAppReset` tears the auto-save subscription down and nothing
      // re-arms it (App's init effect already ran), so a spec that resets state
      // has no auto-save at all afterwards. Any spec whose subject IS the debounce
      // (e.g. "the save that lands on the workspace left by dismissing the
      // startup dialog") has to bring it back explicitly.
      initializeSessionAutoSave: typeof initializeSessionAutoSave;
      // Lazy connect on resume keeps its deferred set in module scope, which no
      // store reset reaches — `resetApp` clears it through here.
      clearLazyConnectState: typeof clearLazyConnectState;
      /** Coverage-harness parse verdict for PostgreSQL (see main.tsx). */
      syntaxProbe?: { parsePostgres: (sql: string) => Promise<{ hasError: boolean }> };
    };
  }
}

if (typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode()) {
  window.__TEST_STORES__ = {
    appStore: useAppStoreForTest,
    sessionStore: useSessionStore,
    sessionsUIStore: useSessionsUIStore,
    workspaceStore: useWorkspaceStore,
    databaseUIStore: useDatabaseUIStore,
    databaseStore: useDatabaseStore,
    connectionsUIStore: useConnectionsUIStore,
    connectionStore: useConnectionStore,
    terminalStore: useTerminalStore,
    aiLockStore: useAiLockStore,
    aiSelectionStore: useAiSelectionStore,
    licenseStore: useLicenseStore,
    authStore: useAuthStore,
    subscriptionStore: useSubscriptionStore,
    proUpgradePromptStore: useProUpgradePromptStore,
    contextSelectionStore: useContextSelectionStore,
    sidebarStore: useSidebarStore,
    sqlSnippetsStore: useSqlSnippetsStore,
    scriptBrowserStore: useScriptBrowserStore,
    mountedFolderIndexStore: useMountedFolderIndexStore,
    aiChatStore: useAiChatStore,
    packagePinsStore: usePackagePinsStore,
    queryHistoryStore: useQueryHistoryStore,
    keybindingsStore: useKeybindingsStore,
    tabHistoryStore: useTabHistoryStore,
    tableExplorerDialogStore: useTableExplorerDialogStore,
    vqeDdlStore: useVqeDdlStore,
    vqeStore: useVqeStore,
    metaInfoManagerDialogStore: useMetaInfoManagerDialogStore,
    databaseBlueprintSetupStore: useDatabaseBlueprintSetupStore,
    syncDataProgressDialogStore: useSyncDataProgressDialogStore,
    pendingRetryStore: usePendingRetryStore,
    schemaCompareOptionDefaultsStore: useSchemaCompareOptionDefaultsStore,
    openSchemaCompareWithFolderAsSource,
    injectPgSchemaForTest,
    dropPgSchema,
    invalidateInsertHintColumns,
    injectRoutineForTest,
    invalidateRoutineCache,
    openDataImportTab,
    dataImportStore: useDataImportStore,
    routeDroppedFiles,
    storageDownloadRequestProbe,
    executeObjectAction,
    triggerAppReset: triggerTestReset,
    initializeSessionAutoSave,
    clearLazyConnectState,
  };
}
